import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Facebook,
  Linkedin,
  Instagram,
  Mail,
  Phone,
  MapPin,
  Send,
} from "lucide-react";
import { siteSettingsAPI } from "../../services/api";

const DEFAULT_SETTINGS = {
  phone_primary: "+91 94370 45109",
  phone_secondary: "+91 91240 75890",
  whatsapp_number: "+91 83277 41261",
  email: "info@lohamart.in",
  address: "S3/16, Industrial Estate Kalunga, Rourkela, Odisha 769004",
  facebook_url: "https://www.facebook.com/share/1L2oqnoYTX/",
  instagram_url:
    "https://www.instagram.com/lohamartindia?igsh=dm55OXNjd3luZ3Nv",
  linkedin_url: "",
};

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  useEffect(() => {
    siteSettingsAPI
      .getSettings()
      .then((res) => {
        const data = res.data.data;
        setSettings({
          phone_primary: data.phone_primary || DEFAULT_SETTINGS.phone_primary,
          phone_secondary:
            data.phone_secondary || DEFAULT_SETTINGS.phone_secondary,
          whatsapp_number:
            data.whatsapp_number || DEFAULT_SETTINGS.whatsapp_number,
          email: data.email || DEFAULT_SETTINGS.email,
          address: data.address || DEFAULT_SETTINGS.address,
          facebook_url: data.facebook_url || DEFAULT_SETTINGS.facebook_url,
          instagram_url: data.instagram_url || DEFAULT_SETTINGS.instagram_url,
          linkedin_url: data.linkedin_url || DEFAULT_SETTINGS.linkedin_url,
        });
      })
      .catch(() => {
        // Keep defaults if the settings API is unreachable
      });
  }, []);

  const quickLinks = [
    { name: "Home", path: "/" },
    { name: "Products", path: "/products" },
    { name: "About Us", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  const categories = [
    { name: "Mild Steel", path: "/products?category=mild-steel" },
    { name: "Stainless Steel", path: "/products?category=stainless-steel" },
    { name: "Non Ferrous", path: "/products?category=non-ferrous" },
    { name: "Pipes & Tubes", path: "/products?category=pipes-tubes" },
  ];

  const policies = [
    { name: "Privacy Policy", path: "/privacy" },
    { name: "Terms & Conditions", path: "/terms" },
    { name: "Return & Refund", path: "/returns" },
    { name: "Contact Us", path: "/contact" },
    { name: "Delete Account", path: "/delete-account" },
  ];

  const socialLinks = [
    { icon: Facebook, href: settings.facebook_url, label: "Facebook" },
    {
      icon: WhatsAppIcon,
      href: settings.whatsapp_number
        ? `https://wa.me/${settings.whatsapp_number.replace(/\D/g, "")}`
        : "",
      label: "WhatsApp",
    },
    { icon: Linkedin, href: settings.linkedin_url, label: "LinkedIn" },
    { icon: Instagram, href: settings.instagram_url, label: "Instagram" },
  ].filter((social) => !!social.href);

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center group">
              <img
                src="/logo.png"
                alt="Loha Mart"
                className="h-10 w-auto object-contain"
              />
            </Link>
            <p className="text-sm text-gray-300 leading-relaxed">
              Your strategic materials partner for steel, structures, and
              construction chemicals. Backed by 10+ years of collective
              experience.
            </p>
            <div className="space-y-2">
              {settings.address && (
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-secondary-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-300">
                    {settings.address}
                  </span>
                </div>
              )}
              {settings.phone_primary && (
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-secondary-500 flex-shrink-0" />
                  <a
                    href={`tel:${settings.phone_primary.replace(/\s/g, "")}`}
                    className="text-sm text-gray-300 hover:text-white transition-colors"
                  >
                    {settings.phone_primary}
                  </a>
                </div>
              )}
              {settings.phone_secondary && (
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-secondary-500 flex-shrink-0" />
                  <a
                    href={`tel:${settings.phone_secondary.replace(/\s/g, "")}`}
                    className="text-sm text-gray-300 hover:text-white transition-colors"
                  >
                    {settings.phone_secondary}
                  </a>
                </div>
              )}
              {settings.email && (
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-secondary-500 flex-shrink-0" />
                  <a
                    href={`mailto:${settings.email}`}
                    className="text-sm text-gray-300 hover:text-white transition-colors"
                  >
                    {settings.email}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">
              Quick Links
            </h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-gray-400 hover:text-primary-500 transition-colors duration-300 inline-block hover:translate-x-1 transform"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">
              Categories
            </h3>
            <ul className="space-y-2">
              {categories.map((category) => (
                <li key={category.path}>
                  <Link
                    to={category.path}
                    className="text-sm text-gray-400 hover:text-primary-500 transition-colors duration-300 inline-block hover:translate-x-1 transform"
                  >
                    {category.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">
              Stay Updated
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              Subscribe to our newsletter for the latest updates and offers.
            </p>
            <form className="space-y-3">
              <div className="relative">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all duration-300"
                />
              </div>
              <button
                type="submit"
                className="w-full px-4 py-2.5 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-lg font-medium hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <span>Subscribe</span>
                <Send className="w-4 h-4" />
              </button>
            </form>
            <div className="flex space-x-3 mt-6">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    aria-label={social.label}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-primary-600 transition-all duration-300 transform hover:-translate-y-1"
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-400">
              &copy; {currentYear} Loha Mart. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {policies.map((policy, index) => (
                <React.Fragment key={policy.path}>
                  <Link
                    to={policy.path}
                    className="text-sm text-gray-400 hover:text-primary-500 transition-colors duration-300"
                  >
                    {policy.name}
                  </Link>
                  {index < policies.length - 1 && (
                    <span className="text-gray-600">|</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
